import { z } from 'zod';

/**
 * Feature requests — in-product "Report missing feature" pipeline.
 *
 * The flow-builder editor toolbar exposes a Report button + modal
 * that lets any signed-in user file a feature request without
 * leaving the canvas. The submission lands in the `feature_requests`
 * table; admins triage from the admin-console; promoted requests
 * get a stable slug captured back into
 * [`docs/handbook/features/pending-tracker.md`](../../../docs/handbook/features/pending-tracker.md).
 *
 * Why one contract file:
 *
 * 1. The user-side POST body, the admin-side PATCH body, and the
 *    full row shape all share enums (persona / category / severity /
 *    status). Consolidating them avoids drift between Modal,
 *    triage panel, route handler, and migration.
 * 2. The two write paths (`feature.requested`, `feature.promoted` /
 *    `feature.declined`) ride on the existing `audit_log` table,
 *    so we only need contract types here — no new audit-log schema.
 *
 * Hard limits:
 *
 * - title: 3-120 chars (DB CHECK enforces too).
 * - description: 10-2000 chars (DB CHECK enforces too).
 * - declineReason: ≤ 500 chars (only required when status=declined).
 * - promotedToSlug: ≤ 80 chars (matches handbook slug convention).
 */

export const FeatureRequestPersonaSchema = z.enum(['manual', 'business', 'qa', 'dev', 'operator']);
export type FeatureRequestPersona = z.infer<typeof FeatureRequestPersonaSchema>;

export const FeatureRequestCategorySchema = z.enum(['editor', 'runner', 'admin', 'reporting', 'api', 'other']);
export type FeatureRequestCategory = z.infer<typeof FeatureRequestCategorySchema>;

export const FeatureRequestSeveritySchema = z.enum(['p0', 'p1', 'p2', 'watch']);
export type FeatureRequestSeverity = z.infer<typeof FeatureRequestSeveritySchema>;

export const FeatureRequestStatusSchema = z.enum(['new', 'triaged', 'promoted', 'declined']);
export type FeatureRequestStatus = z.infer<typeof FeatureRequestStatusSchema>;

/**
 * Wire shape returned by `GET /api/feature-requests`. All
 * server-managed timestamps are ISO strings so the wire stays
 * JSON-pure; the DB stores them as `timestamptz`.
 */
export const FeatureRequestSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(3).max(120),
  description: z.string().min(10).max(2000),
  persona: FeatureRequestPersonaSchema,
  category: FeatureRequestCategorySchema,
  severity: FeatureRequestSeveritySchema,
  pageUrl: z.string().url().max(2048).nullable().optional(),
  reporterUserId: z.string().uuid().nullable().optional(),
  reporterOrgId: z.string().uuid().nullable().optional(),
  status: FeatureRequestStatusSchema,
  promotedToSlug: z.string().max(80).nullable().optional(),
  declineReason: z.string().max(500).nullable().optional(),
  createdAt: z.string(),
  triagedAt: z.string().nullable().optional(),
  promotedAt: z.string().nullable().optional(),
});
export type FeatureRequest = z.infer<typeof FeatureRequestSchema>;

/**
 * `POST /api/feature-requests` body — the subset of the row a
 * client is allowed to set. Server fills `id`, `reporterUserId`,
 * `reporterOrgId`, `status='new'`, `createdAt`.
 */
export const CreateFeatureRequestSchema = z.object({
  title: z.string().min(3).max(120),
  description: z.string().min(10).max(2000),
  persona: FeatureRequestPersonaSchema,
  category: FeatureRequestCategorySchema,
  severity: FeatureRequestSeveritySchema,
  pageUrl: z.string().url().max(2048).optional(),
});
export type CreateFeatureRequest = z.infer<typeof CreateFeatureRequestSchema>;

/**
 * `POST /api/feature-requests` response. Minimal so we don't leak
 * other reporters' rows back to a non-admin caller.
 */
export const CreateFeatureRequestResponseSchema = z.object({
  id: z.string().uuid(),
  status: FeatureRequestStatusSchema,
  createdAt: z.string(),
});
export type CreateFeatureRequestResponse = z.infer<typeof CreateFeatureRequestResponseSchema>;

/**
 * `GET /api/feature-requests?status=new` query schema.
 */
export const ListFeatureRequestsQuerySchema = z.object({
  status: FeatureRequestStatusSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type ListFeatureRequestsQuery = z.infer<typeof ListFeatureRequestsQuerySchema>;

export const ListFeatureRequestsResponseSchema = z.object({
  items: z.array(FeatureRequestSchema),
});
export type ListFeatureRequestsResponse = z.infer<typeof ListFeatureRequestsResponseSchema>;

/**
 * Admin triage decision — `PATCH /api/feature-requests/:id`.
 *
 * Two valid shapes:
 *   - decision='promote' + promotedToSlug → flips status to
 *     'promoted', sets promotedAt + promotedToSlug.
 *   - decision='decline' + declineReason → flips status to
 *     'declined', sets triagedAt + declineReason.
 */
export const PromoteFeatureRequestBodySchema = z.object({
  decision: z.literal('promote'),
  promotedToSlug: z
    .string()
    .min(3)
    .max(80)
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, {
      message: 'Slug must be kebab-case (a-z, 0-9, hyphen).',
    }),
});
export type PromoteFeatureRequestBody = z.infer<typeof PromoteFeatureRequestBodySchema>;

export const DeclineFeatureRequestBodySchema = z.object({
  decision: z.literal('decline'),
  declineReason: z.string().min(3).max(500),
});
export type DeclineFeatureRequestBody = z.infer<typeof DeclineFeatureRequestBodySchema>;

export const TriageFeatureRequestBodySchema = z.discriminatedUnion('decision', [
  PromoteFeatureRequestBodySchema,
  DeclineFeatureRequestBodySchema,
]);
export type TriageFeatureRequestBody = z.infer<typeof TriageFeatureRequestBodySchema>;

export const TriageFeatureRequestResponseSchema = z.object({
  request: FeatureRequestSchema,
});
export type TriageFeatureRequestResponse = z.infer<typeof TriageFeatureRequestResponseSchema>;
